import { useEffect, useRef } from 'react'

import { getProjectSyncHash } from 'screens/YearbookPages'
import {
  useMutation as useLiveblocksMutation,
  useOthersMapped,
  useStorage,
  useUpdateMyPresence
} from 'shared/context/project-room'
import { LiveUser } from 'shared/types/global'

import { DndPage } from '../type'

export const useLiveblocks = ({
  didMountPages,
  groupId,
  dndPages,
  onUpdate
}: {
  didMountPages: React.MutableRefObject<boolean>
  groupId: string
  dndPages: DndPage[]
  onUpdate: () => void
}) => {
  const didMountSync = useRef(false)

  const updateMyPresence = useUpdateMyPresence()

  const othersMapped = useOthersMapped(other => ({
    info: other.presence.info,
    editingPageId: other.presence.editingPageId
  }))

  const syncHash = useStorage(root => root.syncHash)

  const setSyncHash = useLiveblocksMutation(
    ({ storage }, syncHash: string) => {
      storage.set('syncHash', syncHash)
    },
    []
  )

  const editingLiveUsersMap: { [key: string]: LiveUser } =
    othersMapped
      .filter(other => !!other[1].editingPageId)
      .reduce(
        (obj, other) => ({
          ...obj,
          [other[1].editingPageId as string]: {
            ...other[1].info,
            index: other[0]
          }
        }),
        {}
      )

  const itemsHash = getProjectSyncHash(dndPages.map(item => item.id))

  //itemHash is string which join all project ids (for example 'id1|id2|id3')
  //if itemsHash has changed means the user has added/removed or changed project order
  //so we trigger to update the syncHash on liveblocks
  useEffect(() => {
    if (
      !itemsHash ||
      !didMountSync.current ||
      syncHash === null ||
      !didMountPages.current
    ) {
      return
    }
    if (syncHash !== itemsHash) {
      setSyncHash(itemsHash)
    }
  }, [itemsHash])

  //if syncHash is updated and is different from current user itemHash we trigger to refetch the projects
  useEffect(() => {
    if (!didMountSync.current) {
      didMountSync.current = true
      return
    }
    if (syncHash === null || !groupId || !itemsHash) {
      return
    }

    if (syncHash !== itemsHash) {
      onUpdate()
    }
  }, [syncHash])

  return {
    editingLiveUsersMap,
    updateMyPresence: (draggingPreviewUrl: string | null) => {
      if (!didMountSync.current) {
        return
      }
      updateMyPresence({ draggingPreviewUrl })
    }
  }
}
